/* Optional (sunnah / nafl) prayers the user can log alongside the five fard.
   Rakʿah counts are the commonly taught defaults; Ḍuḥā, Tahajjud and Witr let
   the user record how many they prayed. Which ones appear is a setting. */
import type { PrayerKey } from '@shared/prayer';

export type SunnahKey = 'fajrBefore' | 'dhuhrBefore' | 'dhuhrAfter' | 'asrBefore' | 'maghribAfter' | 'ishaAfter' | 'witr' | 'duha' | 'tahajjud';

export interface SunnahDef {
  key: SunnahKey;
  name: string;
  short: string;
  ar: string;
  /** which row it is shown with on the prayer screen */
  with: Exclude<PrayerKey, 'sunrise'> | 'duha' | 'night';
  rakats: number;
  options?: number[];
  /** sunnah muʾakkadah — part of the 12 daily rawātib */
  rawatib?: boolean;
  note: string;
}

export const SUNNAH: SunnahDef[] = [
  { key: 'fajrBefore', name: 'Sunnah of Fajr', short: '2 before', ar: 'سنة الفجر', with: 'fajr', rakats: 2, rawatib: true, note: 'The two rakʿahs before Fajr are "better than the world and all it contains" (Muslim).' },
  { key: 'dhuhrBefore', name: 'Before Dhuhr', short: '4 before', ar: 'قبل الظهر', with: 'dhuhr', rakats: 4, rawatib: true, note: 'Four rakʿahs before Dhuhr.' },
  { key: 'dhuhrAfter', name: 'After Dhuhr', short: '2 after', ar: 'بعد الظهر', with: 'dhuhr', rakats: 2, rawatib: true, note: 'Two rakʿahs after Dhuhr.' },
  { key: 'asrBefore', name: 'Before ʿAsr', short: '4 before', ar: 'قبل العصر', with: 'asr', rakats: 4, note: 'Ghayr muʾakkadah — "May Allah have mercy on one who prays four before ʿAsr" (Abū Dāwūd, Tirmidhī).' },
  { key: 'maghribAfter', name: 'After Maghrib', short: '2 after', ar: 'بعد المغرب', with: 'maghrib', rakats: 2, rawatib: true, note: 'Two rakʿahs after Maghrib.' },
  { key: 'ishaAfter', name: 'After ʿIshāʾ', short: '2 after', ar: 'بعد العشاء', with: 'isha', rakats: 2, rawatib: true, note: 'Two rakʿahs after ʿIshāʾ.' },
  { key: 'witr', name: 'Witr', short: 'Witr', ar: 'الوتر', with: 'isha', rakats: 3, options: [1, 3, 5, 7, 9, 11], note: 'An odd number of rakʿahs after ʿIshāʾ, before Fajr.' },
  { key: 'duha', name: 'Ḍuḥā', short: 'Ḍuḥā', ar: 'الضحى', with: 'duha', rakats: 2, options: [2, 4, 6, 8, 12], note: 'From about 15–20 minutes after sunrise until shortly before Dhuhr.' },
  { key: 'tahajjud', name: 'Tahajjud / Qiyām', short: 'Tahajjud', ar: 'التهجد', with: 'night', rakats: 8, options: [2, 4, 6, 8, 10, 12], note: 'Night prayer after sleeping, best in the last third of the night. Logged on the day whose Fajr follows it.' },
];

export const sunnahDef = (k: SunnahKey) => SUNNAH.find((s) => s.key === k)!;
export const RAWATIB: SunnahKey[] = SUNNAH.filter((s) => s.rawatib).map((s) => s.key);

export const DEFAULT_TRACK_SUNNAH: Record<SunnahKey, boolean> = {
  fajrBefore: true, dhuhrBefore: true, dhuhrAfter: true, asrBefore: false, maghribAfter: true, ishaAfter: true, witr: true, duha: true, tahajjud: true,
};
